import type { z } from 'zod';
import { tokenStore } from '../auth/tokenStore';
import { getActiveCulture } from '../i18n/activeCulture';

const configuredBase: unknown = import.meta.env.VITE_API_BASE_URL;
export const API_BASE_URL: string =
  typeof configuredBase === 'string' && configuredBase.length > 0
    ? configuredBase.replace(/\/$/, '')
    : 'http://localhost:8080';

export const AUTH_EXPIRED_EVENT = 'invite:auth-expired';
export const MAINTENANCE_EVENT = 'invite:maintenance';

/**
 * `keepSessionOn401`: the call's 401 means "wrong credentials" (e.g. a wrong activation code),
 * not an expired session, so the token is kept and no auth-expired event fires.
 */
export type ApiRequestInit = RequestInit & { keepSessionOn401?: boolean };

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

const messageFrom = (body: unknown, fallback: string): string => {
  if (typeof body === 'object' && body !== null) {
    const record = body as Record<string, unknown>;
    if (typeof record['message'] === 'string') {
      return record['message'];
    }
    const nested = record['error'];
    if (typeof nested === 'object' && nested !== null) {
      const message = (nested as Record<string, unknown>)['message'];
      if (typeof message === 'string') {
        return message;
      }
    }
  }
  return fallback;
};

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

const request = async <T>(
  method: Method,
  path: string,
  body: unknown,
  schema: z.ZodType<T> | undefined,
  apiInit?: ApiRequestInit
): Promise<T> => {
  const { keepSessionOn401 = false, ...init } = apiInit ?? {};
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Authorization')) {
    const token = tokenStore.getToken();
    if (token !== null) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }
  if (!headers.has('Accept-Language')) {
    const culture = getActiveCulture();
    if (culture !== null) {
      headers.set('Accept-Language', culture);
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (response.status === 401 && !keepSessionOn401) {
    tokenStore.clear();
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  }
  if (response.status === 503) {
    window.dispatchEvent(new CustomEvent(MAINTENANCE_EVENT));
  }
  if (!response.ok) {
    throw new ApiError(response.status, messageFrom(parsed, response.statusText), parsed);
  }
  return schema === undefined ? (parsed as T) : schema.parse(parsed);
};

export const apiGet = <T>(path: string, schema: z.ZodType<T>, init?: ApiRequestInit): Promise<T> =>
  request('GET', path, undefined, schema, init);

export const apiSend = <T = unknown>(
  method: Exclude<Method, 'GET'>,
  path: string,
  body?: unknown,
  schema?: z.ZodType<T>,
  init?: ApiRequestInit
): Promise<T> => request(method, path, body, schema, init);

export const toQuery = (params: Record<string, string | number | boolean | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs.length > 0 ? `?${qs}` : '';
};
